import { isArray } from 'lodash';
import { IMAGE_URL } from '../../../constants';
import { DefaultCurrencyToISO } from '../../../helpers/currencyCode';
import { ICar } from '../../../interface/car';
import {
    RemoveWhiteSpacesFromString,
    ReplaceWhiteSpaceWithDashString
} from '../jsHelpers';
import { VehicleStructuredData } from './structuredDataTypes';

function getImageURL(img: string): string {
    if (!img) return '';
    // Avoid double slashes
    return `${IMAGE_URL.replace(/\/$/, '')}/${img.replace(/^\//, '')}`;
}

const mapDrivetrainToSchemaUrl = (drivetrain: string): string => {
    const drivetrainLower = drivetrain.toLowerCase().trim();

    switch (drivetrainLower) {
        case 'fwd':
            return 'https://schema.org/FrontWheelDriveConfiguration';
        case 'rwd':
            return 'https://schema.org/RearWheelDriveConfiguration';
        case 'awd':
        case 'eawd':
        case 'rwd-awd hybrid':
        case 'torque-vectoring awd':
            return 'https://schema.org/AllWheelDriveConfiguration';
        case '4wd':
            return 'https://schema.org/FourWheelDriveConfiguration';
        default:
            return 'https://schema.org/FrontWheelDriveConfiguration';
    }
};

export const generateVehicleStructuredData = (
    vehicle: ICar
): VehicleStructuredData => {
    return {
        '@context': 'https://schema.org',
        '@type': 'Car',
        // required fields
        name: vehicle.name || '',
        brand: {
            '@type': 'Brand',
            name: vehicle.brand?.brand || ''
        },
        itemCondition: vehicle.isNewCar
            ? 'https://schema.org/NewCondition'
            : 'https://schema.org/UsedCondition',
        mileageFromOdometer: {
            '@type': 'QuantitativeValue',
            value: vehicle.mileage || vehicle.drivenKM || 0,
            unitCode: 'KMT'
        },
        model: vehicle.model?.model ? String(vehicle.model.model) : '',
        vehicleIdentificationNumber: vehicle.vin || '',
        vehicleModelDate: vehicle.modelYear || vehicle.yearOfManufacture || '',

        // recommended fields
        bodyType: vehicle.bodyType?.details?.[0]?.bodyType || undefined,
        color: vehicle.exteriorColor?.details?.[0]?.color || '',
        vehicleEngine: {
            '@type': 'EngineSpecification',
            fuelType: vehicle.fuelType || ''
        },
        driveWheelConfiguration: vehicle.driveTrain
            ? mapDrivetrainToSchemaUrl(vehicle.driveTrain)
            : 'https://schema.org/FrontWheelDriveConfiguration',
        url: `${
            process.env.REACT_APP_FRONT_END_URL
        }/buy/${RemoveWhiteSpacesFromString(
            vehicle.brand?.brand
        )}/${ReplaceWhiteSpaceWithDashString(vehicle?.model?.model)}/${
            vehicle.modelYear
        }/${vehicle.price}/${vehicle.uId}`,

        vehicleConfiguration: vehicle.trim || '',
        numberOfDoors: vehicle.noOfDoors || 5,
        vehicleTransmission: vehicle.transmission || '',
        vehicleInteriorColor:
            vehicle.interiorColor?.details?.[0]?.color || undefined,
        vehicleInteriorType: vehicle.interiorType || '',

        offers: {
            '@type': 'Offer',
            price: vehicle.price || 0,
            priceCurrency:
                DefaultCurrencyToISO.find(
                    (item) =>
                        item.defaultCurrency ===
                        process.env.REACT_APP_DEFAULT_CURRENCY
                )?.currencyISOCode || 'N/A',
            availability:
                vehicle.status === 'sold'
                    ? 'https://schema.org/OutOfStock'
                    : 'https://schema.org/InStock',
            seller: {
                '@type': 'Organization',
                name: 'Auto24'
            }
        },
        image: isArray(vehicle.images)
            ? (vehicle.images as string[])
                  ?.filter((img): img is string => img !== null)
                  ?.map((img) => getImageURL(img)) || []
            : [getImageURL(vehicle.images ?? '')],
        description: typeof vehicle.details === 'string' ? vehicle.details : '',
        vehicleSeatingCapacity: vehicle.noOfSeats || undefined
    };
};
